import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Phone, MessageSquare, Users, Zap, Target, TrendingUp } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';

const Home = () => {
  const { t } = useLanguage();

  const services = [
    {
      icon: <Phone className="w-12 h-12 text-green-400" />,
      title: t.services.speedToLead.title,
      subtitle: t.services.speedToLead.subtitle,
      description: t.services.speedToLead.description,
      gradient: 'from-green-600/20 to-emerald-600/20'
    },
    {
      icon: <MessageSquare className="w-12 h-12 text-green-400" />,
      title: t.services.textAgent.title,
      subtitle: t.services.textAgent.subtitle,
      description: t.services.textAgent.description,
      gradient: 'from-green-500/20 to-teal-600/20'
    },
    {
      icon: <Users className="w-12 h-12 text-green-400" />,
      title: t.services.recovery.title,
      subtitle: t.services.recovery.subtitle,
      description: t.services.recovery.description,
      gradient: 'from-emerald-600/20 to-green-500/20'
    }
  ];

  const integrations = [
    { name: 'Salesforce', logo: 'https://upload.wikimedia.org/wikipedia/commons/f/f9/Salesforce.com_logo.svg' },
    { name: 'HubSpot', logo: 'https://www.hubspot.com/hubfs/HubSpot_Logos/HubSpot-Inversed-Favicon.png' },
    { name: 'Slack', logo: 'https://upload.wikimedia.org/wikipedia/commons/b/b9/Slack_Technologies_Logo.svg' },
    { name: 'Google', logo: 'https://upload.wikimedia.org/wikipedia/commons/2/2f/Google_2015_logo.svg' },
    { name: 'Meta', logo: 'https://upload.wikimedia.org/wikipedia/commons/7/7b/Meta_Platforms_Inc._logo.svg' },
    { name: 'Mailchimp', logo: 'https://upload.wikimedia.org/wikipedia/commons/3/3e/Mailchimp_Logo.svg' }
  ];

  const stats = [
    { icon: <Zap className="w-8 h-8 text-green-400" />, value: '<1 min', label: 'Tiempo de respuesta' },
    { icon: <Target className="w-8 h-8 text-green-400" />, value: '95%', label: 'Tasa de contacto' },
    { icon: <TrendingUp className="w-8 h-8 text-green-400" />, value: '3x', label: 'Más conversiones' }
  ];

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-green-900/20 via-black to-emerald-900/20" />
        <div className="absolute top-20 right-0 w-96 h-96 bg-green-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500/10 rounded-full blur-3xl" />
        
        <div className="container mx-auto px-4 py-20 md:py-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight">
              {t.hero.title}{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                {t.hero.titleHighlight}
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-200 mb-8 max-w-3xl mx-auto">
              {t.hero.subtitle}
            </p>
            <Link to="/demo">
              <Button className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white rounded-full px-8 py-6 text-lg flex items-center space-x-2 mx-auto shadow-lg shadow-green-600/50 transition-all duration-200 hover:scale-105">
                <span>{t.hero.cta}</span>
                <ArrowRight className="w-5 h-5" />
              </Button>
            </Link>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-20 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <Card key={index} className="bg-gradient-to-br from-gray-900 to-black border-green-900/30 hover:border-green-600/50 transition-all duration-300">
                <CardContent className="p-6 text-center">
                  <div className="flex justify-center mb-4">{stat.icon}</div>
                  <div className="text-3xl font-bold text-green-400 mb-2">{stat.value}</div>
                  <div className="text-gray-200">{stat.label}</div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-20 bg-gradient-to-b from-black to-gray-900">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="bg-green-600/20 text-green-400 border-green-600/30 mb-4 px-4 py-2 text-sm">
              {t.services.badge}
            </Badge>
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">
                {t.services.title}
              </span>{' '}
              {t.services.titleEnd}
            </h2>
            <p className="text-xl text-gray-200 max-w-3xl mx-auto">
              {t.services.subtitle}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <Card 
                key={index} 
                className="bg-gradient-to-br from-gray-900 to-black border-green-900/30 hover:border-green-600/50 transition-all duration-300 hover:scale-105 group"
              >
                <CardContent className="p-8">
                  <div className={`w-20 h-20 rounded-2xl bg-gradient-to-br ${service.gradient} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    {service.icon}
                  </div>
                  <Badge className="bg-green-600/20 text-green-400 border-green-600/30 mb-3">
                    {service.subtitle}
                  </Badge>
                  <h3 className="text-2xl font-bold mb-4 text-white">{service.title}</h3>
                  <p className="text-gray-200 leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations Section */}
      <section className="py-20 bg-black">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl md:text-4xl font-bold text-center mb-12">
            {t.integrations.title}
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-12 max-w-5xl mx-auto">
            {integrations.map((integration, index) => (
              <div 
                key={index} 
                className="grayscale hover:grayscale-0 opacity-50 hover:opacity-100 transition-all duration-300 hover:scale-110"
              >
                <img 
                  src={integration.logo} 
                  alt={integration.name} 
                  className="h-8 md:h-10 w-auto"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-br from-green-900/20 via-black to-emerald-900/20">
        <div className="container mx-auto px-4">
          <Card className="bg-gradient-to-br from-gray-900 to-black border-green-600/30 max-w-4xl mx-auto">
            <CardContent className="p-12 text-center">
              <h2 className="text-3xl md:text-5xl font-bold mb-4 text-gray-200">
                {t.cta.title}
              </h2>
              <p className="text-xl text-gray-200 mb-8">
                {t.cta.subtitle}
              </p>
              <Link to="/demo">
                <Button className="bg-gradient-to-r from-green-600 to-green-500 hover:from-green-500 hover:to-green-400 text-white rounded-full px-8 py-6 text-lg flex items-center space-x-2 mx-auto shadow-lg shadow-green-600/50 transition-all duration-200 hover:scale-105">
                  <span>{t.cta.button}</span>
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Home;